import { matchPath } from "react-router-dom";
import ConfigData from "../Config/config.json";
import PageRoutes from "./constants";

/**
 * HTPTV-714 — descriptive, per-page document titles for GA4 page-level reporting.
 *
 * Every page used to report the same title (the tenant brand, e.g. "NATV") in
 * GA4 because `document.title` was set once and never changed, collapsing all
 * pageviews into a single row. This builds a distinct, tenant-prefixed title per
 * route, e.g. "NATV - Plans" / "NATV - Watch: Game 12345". GA4 Enhanced
 * Measurement reads `document.title` for each `page_view`, so keeping it current
 * per route (see <RouteTitle/>) makes the "Views by Page title" report usable.
 *
 * Shared codebase: the same map applies to all tenants; only the brand prefix
 * differs (from config). Watch pages carry the game_id (not team names) because
 * it is available synchronously from the URL and aligns with game-level data.
 */

export const TITLE_SEPARATOR = " - ";

type LabelFn = (params: Record<string, string | undefined>) => string;

// Ordered, most-specific-first. Each entry maps a route pattern (from
// PageRoutes, the single source of truth) to a static label or a function of
// the matched params. Covers every registered <Route> in App.tsx; the whole
// /admin subtree intentionally shares one label, and any path not listed here
// falls through to the humanized fallback in getPageLabel.
const ROUTE_TITLES: Array<{ pattern: string; label: string | LabelFn }> = [
  { pattern: PageRoutes.HomePage, label: "Home" },
  { pattern: PageRoutes.Packages, label: "Plans" },
  { pattern: PageRoutes.Watch, label: (p) => `Watch: Game ${p.gameId}` },
  { pattern: PageRoutes.OnDemand, label: "On Demand" },
  { pattern: PageRoutes.UpcomingEvent, label: "Upcoming Events" },
  { pattern: PageRoutes.Login, label: "Sign In" },
  { pattern: PageRoutes.Register, label: "Create Account" },
  { pattern: PageRoutes.PasswordReset, label: "Reset Password" },
  { pattern: PageRoutes.VerifyEmail, label: "Verify Email" },
  { pattern: PageRoutes.AccountPage, label: "Account" },
  { pattern: PageRoutes.CancelSaveFlow, label: "Plans & Subscription" },
  { pattern: PageRoutes.EditProfile, label: "Edit Profile" },
  { pattern: PageRoutes.AccountSettings, label: "Account Settings" },
  { pattern: PageRoutes.Setting, label: "Profile" },
  { pattern: PageRoutes.GiftPage, label: "Buy Gift" },
  { pattern: PageRoutes.TermAndConditions, label: "Terms and Conditions" },
  { pattern: PageRoutes.PrivacyPolicy, label: "Privacy Policy" },
  { pattern: PageRoutes.ContactUS, label: "Support" },
  { pattern: PageRoutes.ShortContent, label: "Highlights" },
  { pattern: PageRoutes.ShortView, label: "Short Content: View" },
  { pattern: PageRoutes.FilterShortContent, label: "Short Content" },
  { pattern: PageRoutes.HighLightPlayer, label: "Highlight Player" },
  { pattern: PageRoutes.Team, label: "Team" },
  { pattern: PageRoutes.MyNAHLTV, label: "Build a Spotlight" },
  { pattern: PageRoutes.PersonalChannel, label: "Spotlight" },
  { pattern: PageRoutes.AcceptChannelShare, label: "Spotlight Share: Accept" },
  { pattern: PageRoutes.DeclineChannelShare, label: "Spotlight Share: Decline" },
  { pattern: PageRoutes.paymentPage, label: "Payment" },
  { pattern: PageRoutes.Alert, label: "Notifications" },
  // All /admin/* routes (Admin, Users, Media, Ads, Polls, Reports, Games,
  // Rules, Whizzard, Slider, GameSummary, UserView, etc.) report under one
  // "Admin" title. No single PageRoutes constant spans the subtree, so the
  // shared prefix is matched directly.
  { pattern: "/admin/*", label: "Admin" },
];

/** Title-case a path segment: "some-new-page" -> "Some New Page". */
const humanize = (segment: string): string =>
  segment
    .replace(/[-_]+/g, " ")
    .split(" ")
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");

/** The tenant brand used as the title prefix (e.g. "NATV"); "" if unknown. */
export const getTenantName = (): string => {
  const websiteName = process.env.REACT_APP_ENV as string | undefined;
  const tenant = (
    ConfigData as Record<string, { title?: string; mainName?: string }>
  )?.[websiteName ?? ""];
  return tenant?.title || tenant?.mainName || "";
};

/**
 * The descriptive page label for a pathname, without the tenant prefix.
 * Falls back to a humanized first path segment so no route ever collapses.
 */
export const getPageLabel = (pathname: string): string => {
  for (const { pattern, label } of ROUTE_TITLES) {
    const match = matchPath(pattern, pathname);
    if (match) return typeof label === "function" ? label(match.params) : label;
  }
  const firstSegment = pathname.split("/").filter(Boolean)[0] ?? "";
  return firstSegment ? humanize(firstSegment) : "Home";
};

/**
 * The full document title for a pathname: "{Tenant} - {Label}".
 * Drops the prefix when the tenant brand is unknown.
 */
export const getPageTitle = (pathname: string): string => {
  const label = getPageLabel(pathname);
  const tenant = getTenantName();
  if (!tenant) return label;
  if (!label) return tenant;
  return `${tenant}${TITLE_SEPARATOR}${label}`;
};
